/**
 * Rating preset definitions for survey rating questions.
 *
 * Each preset defines:
 * - label: display name in the editor
 * - getIcon(index, maxRating, isSelected): returns the emoji/character for that position
 * - getColor(index, maxRating): returns CSS color for gradient effect (low = red/bad, high = green/good)
 *
 * The `index` is 0-based (0 = first/lowest rating).
 */

/**
 * Generate a gradient color from red (bad) to green (good).
 * @param {number} index - 0-based position
 * @param {number} total - total number of rating levels
 * @returns {string} CSS color string
 */
function redToGreenGradient(index, total) {
  if (total <= 1) return '#48bb78'
  const t = index / (total - 1)
  // Red → Orange → Yellow → Light Green → Green
  const r = Math.round(229 - t * 157) // 229 → 72
  const g = Math.round(62 + t * 125) // 62 → 187
  const b = Math.round(62 + t * 58) // 62 → 120
  return `rgb(${r}, ${g}, ${b})`
}

/**
 * Gray to red gradient (for hearts).
 */
function grayToRedGradient(index, total) {
  if (total <= 1) return '#e53e3e'
  const t = index / (total - 1)
  const r = Math.round(160 + t * 69) // 160 → 229
  const g = Math.round(174 - t * 112) // 174 → 62
  const b = Math.round(192 - t * 130) // 192 → 62
  return `rgb(${r}, ${g}, ${b})`
}

// Face emojis scaled to any maxRating
const FACE_EMOJIS = ['😡', '😞', '😐', '😊', '😍']
// Thumb emojis
const THUMB_EMOJIS = ['👎', '👊', '✊', '👍', '👍']
// Heart emojis
const HEART_EMOJIS = ['🖤', '🤍', '💛', '🧡', '❤️']

/**
 * Pick an emoji from a fixed set scaled to any maxRating.
 */
function pickEmoji(emojiSet, index, maxRating) {
  const mapped = Math.round((index / (maxRating - 1)) * (emojiSet.length - 1))
  return emojiSet[Math.min(mapped, emojiSet.length - 1)]
}

export const RATING_PRESETS = {
  stars: {
    label: 'Stars',
    preview: '⭐',
    getIcon: (_index, _maxRating, isSelected) => (isSelected ? '★' : '☆'),
    getColor: () => '#f6ad55',
    unselectedColor: '#e2e8f0',
  },
  faces: {
    label: 'Faces',
    preview: '😊',
    getIcon: (index, maxRating) => pickEmoji(FACE_EMOJIS, index, maxRating),
    getColor: (index, maxRating) => redToGreenGradient(index, maxRating),
    unselectedColor: '#e2e8f0',
  },
  thumbs: {
    label: 'Thumbs',
    preview: '👍',
    getIcon: (index, maxRating) => pickEmoji(THUMB_EMOJIS, index, maxRating),
    getColor: (index, maxRating) => redToGreenGradient(index, maxRating),
    unselectedColor: '#e2e8f0',
  },
  hearts: {
    label: 'Hearts',
    preview: '❤️',
    getIcon: (index, maxRating) => pickEmoji(HEART_EMOJIS, index, maxRating),
    getColor: (index, maxRating) => grayToRedGradient(index, maxRating),
    unselectedColor: '#e2e8f0',
  },
}

export const RATING_STYLE_OPTIONS = [
  { value: 'stars', label: 'Stars ⭐' },
  { value: 'faces', label: 'Faces 😊' },
  { value: 'thumbs', label: 'Thumbs 👍' },
  { value: 'hearts', label: 'Hearts ❤️' },
  { value: 'custom', label: 'Custom Images' },
]

/**
 * Get the preset config, falling back to stars for unknown styles.
 */
export function getRatingPreset(style) {
  return RATING_PRESETS[style] || RATING_PRESETS.stars
}
